/**
 * Step configuration for the StorageForm wizard
 * Centralizes step metadata for easy modification and maintenance
 */

export interface StepConfig {
  id: number
  title: string
  description?: string
}

/**
 * Array of step configurations for the storage form
 * This makes it easy to:
 * - Modify step order
 * - Add/remove steps
 * - Conditionally skip steps for authenticated users
 */
export const STORAGE_FORM_STEPS: StepConfig[] = [
  {
    id: 1,
    title: 'Personal Information',
    description: 'Basic contact information'
  },
  {
    id: 2,
    title: 'Address Information',
    description: 'Physical address and driver license'
  },
  {
    id: 3,
    title: 'Vehicle Information',
    description: 'Vehicle details and insurance'
  },
  {
    id: 4,
    title: 'Emergency Contact',
    description: 'Emergency contact information'
  },
  {
    id: 5,
    title: 'Lease Details',
    description: 'Move-in date and access options'
  },
  {
    id: 6,
    title: 'Agreement & Pricing',
    description: 'Review pricing and agree to terms'
  }
]

/**
 * Step IDs that should be skipped for authenticated customers
 * These steps contain information we already have from their account
 */
const AUTHENTICATED_SKIP_STEPS = [1, 2, 4] // Personal Info, Address Info, Emergency Contact

/**
 * Get the steps to display based on authentication status
 * @param isAuthenticated - Whether the user is already logged in
 * @returns Array of step configurations to display
 */
export const getActiveSteps = (isAuthenticated: boolean = false): StepConfig[] => {
  if (!isAuthenticated) {
    return STORAGE_FORM_STEPS
  }
  return STORAGE_FORM_STEPS.filter(step => !AUTHENTICATED_SKIP_STEPS.includes(step.id))
}

/**
 * Get the total number of steps based on authentication status
 * @param isAuthenticated - Whether the user is already logged in
 */
export const getTotalSteps = (isAuthenticated: boolean = false) => {
  return getActiveSteps(isAuthenticated).length
}

/**
 * Get step titles as an array (for StepProgressIndicator)
 * @param isAuthenticated - Whether the user is already logged in
 */
export const getStepTitles = (isAuthenticated: boolean = false) => {
  return getActiveSteps(isAuthenticated).map(step => step.title)
}

/**
 * Get step by ID
 */
export const getStepById = (id: number) => STORAGE_FORM_STEPS.find(step => step.id === id)

/**
 * Map current step index to actual step ID based on authentication status
 * For authenticated users, step 1 maps to step 3 (Vehicle Info), step 2 to step 5, etc.
 * @param stepIndex - Current step index (1-based)
 * @param isAuthenticated - Whether the user is already logged in
 * @returns Actual step ID
 */
export const getStepId = (stepIndex: number, isAuthenticated: boolean = false): number => {
  const activeSteps = getActiveSteps(isAuthenticated)
  return activeSteps[stepIndex - 1]?.id || stepIndex
}
